import React from 'react';
import './invoice.css';

const LOGO = '/brand/telecalx-logo.png';
const TAGLINE = 'The Future of Business Calling';
const date = (iso) => (iso ? new Date(`${iso}Z`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const money = (paise) => (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// The invoice's own status (set by the server once a payment is verified): open is due (Pending), paid, failed.
const STATUS = { open: ['Pending', 'pending'], paid: ['Paid', 'paid'], failed: ['Payment failed', 'failed'] };

function Banner({ inv, tone, balance, r, pendingApproval }) {
  if (tone === 'paid') {
    return (
      <section className="tcx-inv-banner paid">
        <span className="tcx-inv-banner-icon" aria-hidden="true">✓</span>
        <div>
          <b>Payment Successful</b>
          <p>Thank you for your payment.{pendingApproval ? ' Your subscription is active and is pending admin approval.' : ''}</p>
        </div>
      </section>
    );
  }
  return (
    <section className={`tcx-inv-banner ${tone}`}>
      <span className="tcx-inv-banner-icon" aria-hidden="true">{tone === 'failed' ? '!' : '₹'}</span>
      <div>
        <b>{tone === 'failed' ? 'Payment Failed' : 'Payment Pending'}</b>
        <p>{tone === 'failed' ? 'The last payment for this invoice did not go through. ' : ''}
          ₹{r(balance)} is due by {date(inv.due_at)}.</p>
      </div>
    </section>
  );
}

/**
 * One invoice as a document: the same component in the Billing & Plan modal and in the printed / saved PDF, so they
 * always match. `page` is GET /api/tenant/invoices/{id}: {invoice, customer, seller, paid_with, how_to_pay}. Always
 * light (it's paper), whatever the dashboard theme.
 */
export default function InvoiceDocument({ page }) {
  const { invoice: inv, customer, seller, paid_with: paidWith } = page;
  const [statusLabel, tone] = STATUS[inv.status] || [inv.status, 'pending'];
  const isPaid = inv.status === 'paid';
  const balance = Math.max(0, inv.total - (inv.paid || 0));
  const howToPay = !isPaid ? (page.how_to_pay || []).filter((m) => m.key !== 'razorpay') : [];
  const pendingApproval = customer?.approval_status === 'pending';
  const cycle = inv.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly';
  const website = seller.website ? seller.website.replace(/^https?:\/\//, '') : '';
  // Amounts from the API are in rupees; shown with paise so the columns line up.
  const r = (rupees) => money(Math.round(rupees * 100));

  return (
    <article className="tcx-invoice" aria-label={`Invoice ${inv.number}`}>
      <header className="tcx-inv-head">
        <div className="tcx-inv-brand">
          <img src={LOGO} alt={`TeleCalX — ${TAGLINE}`} />
        </div>
        <div className="tcx-inv-title">
          <h1>INVOICE</h1>
          <dl>
            <dt>Invoice No:</dt><dd>{inv.number}</dd>
            <dt>Invoice Date:</dt><dd>{date(inv.issued_at)}</dd>
            {isPaid
              ? <><dt>Payment Date:</dt><dd>{date(paidWith?.paid_at || inv.paid_at)}</dd></>
              : <><dt>Due Date:</dt><dd>{date(inv.due_at)}</dd></>}
          </dl>
        </div>
      </header>

      <section className="tcx-inv-parties">
        <div>
          <h2>From</h2>
          <b>{seller.legal_name}</b>
          {seller.address && <p>{seller.address}</p>}
          {seller.gstin && <p className="tcx-inv-gstin">GSTIN: <span>{seller.gstin}</span></p>}
        </div>
        <div>
          <h2>Bill To</h2>
          <b>{customer?.name || inv.tenant_name || '—'}</b>
          {customer?.contact_name && <p>{customer.contact_name}</p>}
          {customer?.contact_email && <p>{customer.contact_email}</p>}
        </div>
      </section>

      <Banner inv={inv} tone={tone} balance={balance} r={r} pendingApproval={pendingApproval} />

      <table className="tcx-inv-lines">
        <thead>
          <tr><th className="idx">#</th><th>Description</th><th className="num">Qty</th><th className="num">Unit Price (₹)</th><th className="num">Amount (₹)</th></tr>
        </thead>
        <tbody>
          {inv.lines.map((l, i) => (
            <tr key={i}>
              <td className="idx">{i + 1}</td>
              <td><b>{l.label}</b>{i === 0 && inv.plan && <small>{cycle} Subscription</small>}</td>
              <td className="num">{l.quantity}</td><td className="num">{r(l.unit_price)}</td><td className="num">{r(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="tcx-inv-summary">
        <dl className="tcx-inv-totals">
          <dt>Subtotal</dt><dd>₹{r(inv.subtotal)}</dd>
          {inv.discount > 0 && <><dt>Discount{inv.coupon_code ? ` (${inv.coupon_code})` : ''}</dt><dd>− ₹{r(inv.discount)}</dd></>}
          <dt>{inv.tax_name} ({inv.tax_rate}%)</dt><dd>₹{r(inv.tax)}</dd>
          <dt className="grand">Total (INR)</dt><dd className="grand">₹{r(inv.total)}</dd>
          {inv.paid > 0 && !isPaid && <><dt>Paid so far</dt><dd>₹{r(inv.paid)}</dd><dt className="due">Balance due</dt><dd className="due">₹{r(balance)}</dd></>}
        </dl>
      </div>

      <section className="tcx-inv-details">
        <h2>Payment Details</h2>
        <dl>
          {paidWith && <><dt>Payment Method</dt><dd>{paidWith.method}</dd></>}
          {paidWith?.reference && <><dt>Transaction ID</dt><dd className="mono">{paidWith.reference}</dd></>}
          <dt>Billing Period</dt><dd>{date(inv.period_start)} – {date(inv.period_end)}</dd>
          <dt>Status</dt><dd><span className={`tcx-inv-status ${tone}`}>{statusLabel}</span></dd>
        </dl>
      </section>

      {howToPay.length > 0 && (
        <section className="tcx-inv-pay">
          <h2>How to pay</h2>
          {howToPay.map((m) => (
            <p key={m.key}><b>{m.name}</b>{Object.entries(m.details).filter(([k, v]) => v && k !== 'instructions').map(([k, v]) => ` · ${k.replaceAll('_', ' ')}: ${v}`)}
              {m.details.instructions && <small>{m.details.instructions}</small>}</p>
          ))}
        </section>
      )}

      <section className="tcx-inv-notes">
        <span className="tcx-inv-notes-icon" aria-hidden="true">i</span>
        <div>
          <h2>Important Notes</h2>
          <ul>
            <li>This is a computer generated invoice and does not require a signature.</li>
            {pendingApproval && <li>Your subscription will be activated after admin approval.</li>}
            <li>For any queries, please contact our support team{seller.support_email ? <> at <b>{seller.support_email}</b></> : ''}.</li>
            {seller.invoice_notes && <li>{seller.invoice_notes}</li>}
          </ul>
        </div>
      </section>

      <footer className="tcx-inv-foot">
        <div><span>Best regards,</span><b>TeleCalX Team</b><span>{TAGLINE}</span></div>
        {website && <div className="right"><b>{website}</b></div>}
      </footer>
    </article>
  );
}
